import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import * as api from '../api/client';
import { AutocompleteInput } from '../components/AutocompleteInput';
import type { FreeTextItem } from '../components/AutocompleteInput';
import { DataSheet } from '../components/DataSheet';
import type { DataSheetColumn } from '../components/DataSheet';
import { CATEGORY_OPTIONS } from '../data/category-labels';
import { escapeHtml } from '../lib/cells';

interface LeadItemDraft {
  /** 수정 모드에서 prefill 시 채워짐. 신규 행은 undefined → 저장 시 INSERT 됨. */
  id?: number;
  category: api.LeadCategory | null;
  option_part: string | null;
  item_description: string | null;
  quantity: number | null;
  /** RFQ 발송 대상 — 시트 토글 컬럼. */
  needs_rfq: boolean;
  notes: string | null;
  [key: string]: unknown;
}

export interface LeadCreateViewOptions {
  /** 'create' (default) — 신규 등록. 'edit' — 기존 리드 수정. */
  mode?: 'create' | 'edit';
  /** edit 모드일 때 prefill 대상 lead id. */
  leadId?: number;
}

interface LeadCreateData {
  customerLabel: string;        // autocomplete 옆 보조 표시 ("✓ ABC 회사 #5" 등)
  customerError: string;        // customer 미선택 시
  inquiry_channel: api.InquiryChannel;
  status: api.LeadStatus;
  customer_contact: string;
  assigned_to: string;
  inquiry_summary: string;
  expected_close_date: string;
  itemCount: number;
  errorMessage: string;
  saving: boolean;
}

/**
 * 🎯 LeadCreateView — /leads/new
 *
 * 회의록 §2.4 + 기획서 §3.4 + Sprint 2 시트 모드.
 */
export class LeadCreateView extends SolaView<LeadCreateData> {
  private readonly mode: 'create' | 'edit';
  private readonly leadId: number | null;
  private selectedCustomer: api.CustomerRow | null = null;
  private items: LeadItemDraft[] = [];

  private readonly customerAC: AutocompleteInput<api.CustomerRow>;
  private readonly itemsSheet: DataSheet<LeadItemDraft>;

  override events = {
    click: {
      '@cancel': 'handleCancel',
      '@save': 'handleSave',
    },
    change: {
      '@channel': 'handleChannelChange',
      '@status': 'handleStatusChange',
    },
    input: {
      '@customer-contact': 'handleCustomerContactInput',
      '@assigned-to': 'handleAssignedToInput',
      '@inquiry-summary': 'handleInquirySummaryInput',
      '@expected-close-date': 'handleExpectedCloseDateInput',
    },
  };

  constructor(opts: LeadCreateViewOptions = {}) {
    super();
    this.mode = opts.mode ?? 'create';
    this.leadId = opts.leadId != null ? Number(opts.leadId) : null;
    this.data = {
      customerLabel: '',
      customerError: '',
      inquiry_channel: 'kakao',
      status: 'inquiry',
      customer_contact: '',
      assigned_to: '',
      inquiry_summary: '',
      expected_close_date: '',
      itemCount: 0,
      errorMessage: '',
      saving: false,
    };

    this.customerAC = new AutocompleteInput<api.CustomerRow>({
      placeholder: '고객사명 입력 (기존 고객 검색 또는 신규 등록)',
      fetchSuggestions: (q, signal) => api.fetchCustomers(q, signal),
      renderItem: (c) => {
        const main = escapeHtml(c.name);
        const sub = c.business_no
          ? ` <span class="ac-dim">${escapeHtml(c.business_no)}</span>`
          : '';
        return `${main}${sub}`;
      },
      allowFreeText: true,
      freeTextLabel: (q) => `＋ "${q}" 신규 customer 로 등록`,
      onSelect: (item) => void this.handleCustomerSelect(item),
    });

    this.itemsSheet = new DataSheet<LeadItemDraft>({
      columns: this.makeItemColumns(),
      // DDL chk_lead_item_has_identifier 와 정렬:
      // option_part / item_description 둘 중 하나가 있어야 의미 있는 행.
      // 카테고리/수량/메모만 있는 행은 빈 행으로 간주 — 새 빈 행 자동 추가 안 함.
      identifyingColumnKeys: ['option_part', 'item_description'],
      onChange: (rows) => {
        this.items = rows;
        this.data.itemCount = rows.length;
        this.notify('itemCount');
      },
    });
  }

  override getTitle(): string {
    if (this.mode === 'edit' && this.leadId != null) {
      return `리드 #${this.leadId} 수정 — HCLTech`;
    }
    return '신규 리드 등록 — HCLTech';
  }

  override onMount(): void {
    if (this.mode === 'edit' && this.leadId != null) {
      void this.prefillFromLead(this.leadId);
    }
  }

  /**
   * 수정 모드 — 서버에서 lead full 가져와서 양식 + 시트 prefill.
   */
  private async prefillFromLead(id: number): Promise<void> {
    try {
      const full = await api.fetchLeadFull(id);

      // 1) 고객사
      if (full.customer) {
        this.selectedCustomer = full.customer;
        this.customerAC.setValue(full.customer.name);
        this.data.customerLabel = `✓ ${full.customer.name} (#${full.customer.id})${full.customer.business_no ? ` · 사업자번호 ${full.customer.business_no}` : ''}`;
        this.notify('customerLabel');
      }

      // 2) lead 본문 필드
      const lead = full.lead as Record<string, unknown>;
      this.data.inquiry_channel = (lead.inquiry_channel as api.InquiryChannel) ?? 'kakao';
      this.data.status = (lead.status as api.LeadStatus) ?? 'inquiry';
      this.data.customer_contact = (lead.customer_contact as string) ?? '';
      this.data.assigned_to = (lead.assigned_to as string) ?? '';
      this.data.inquiry_summary = (lead.inquiry_summary as string) ?? '';
      // expected_close_date 는 'YYYY-MM-DD' 형태 (ISO substr)
      const ecd = lead.expected_close_date;
      this.data.expected_close_date = ecd ? String(ecd).slice(0, 10) : '';

      // 셀렉트/인풋 DOM 강제 동기화 (data-bind 가 없는 form 요소)
      this.syncFormInputs();

      // 3) 품목 — id 보존
      const items: LeadItemDraft[] = full.items.map((it) => {
        const r = it as Record<string, unknown>;
        return {
          id: r.id != null ? Number(r.id) : undefined,
          category: (r.category as api.LeadCategory | null) ?? null,
          option_part: (r.option_part as string | null) ?? null,
          item_description: (r.item_description as string | null) ?? null,
          quantity: r.quantity != null ? Number(r.quantity) : null,
          needs_rfq: r.needs_rfq === true,
          notes: (r.notes as string | null) ?? null,
        };
      });
      this.items = items;
      this.itemsSheet.setRows(items);
      this.data.itemCount = items.length;
      this.notify('itemCount');
    } catch (err) {
      this.setError(`리드 정보 로드 실패: ${(err as Error).message}`);
    }
  }

  /** prefill 후 select/input 의 DOM 값을 data 와 맞춤. */
  private syncFormInputs(): void {
    const root = this._rootElement;
    if (!root) return;
    const chSel = root.querySelector<HTMLSelectElement>('[data-action="channel"]');
    if (chSel) chSel.value = this.data.inquiry_channel;
    const stSel = root.querySelector<HTMLSelectElement>('[data-action="status"]');
    if (stSel) stSel.value = this.data.status;
    const cc = root.querySelector<HTMLInputElement>('[data-action="customer-contact"]');
    if (cc) cc.value = this.data.customer_contact;
    const at = root.querySelector<HTMLInputElement>('[data-action="assigned-to"]');
    if (at) at.value = this.data.assigned_to;
    const isum = root.querySelector<HTMLTextAreaElement>('[data-action="inquiry-summary"]');
    if (isum) isum.value = this.data.inquiry_summary;
    const ecd = root.querySelector<HTMLInputElement>('[data-action="expected-close-date"]');
    if (ecd) ecd.value = this.data.expected_close_date;
  }

  private makeItemColumns(): DataSheetColumn<LeadItemDraft>[] {
    return [
      {
        key: 'category',
        label: '카테고리',
        type: 'dropdown',
        width: 130,
        options: CATEGORY_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
      },
      {
        key: 'option_part',
        label: '제품번호',
        type: 'option-part-search',
        width: 220,
        placeholder: '검색 또는 입력',
      },
      {
        key: 'item_description',
        label: '설명',
        type: 'text',
        width: 240,
        placeholder: '카탈로그 미등록 시 자유 설명',
      },
      {
        key: 'quantity',
        label: '수량',
        type: 'number',
        width: 80,
        align: 'center',
      },
      {
        key: 'needs_rfq',
        label: '견적요청',
        type: 'toggle',
        width: 90,
        align: 'center',
      },
      {
        // UI 표시 전용 — DB 컬럼 아님.
        // 추후 inventory 조회로 채울 자리. 일단 0 고정.
        key: 'current_stock',
        label: '현재 재고',
        type: 'readonly',
        width: 90,
        align: 'center',
        compute: () => '0',
      },
      {
        key: 'notes',
        label: '메모',
        type: 'text',
        width: 200,
      },
    ];
  }

  render(): HTMLElement {
    const isEdit = this.mode === 'edit';
    const heading = isEdit
      ? `✏️ 리드 #${this.leadId} 수정`
      : '📥 신규 리드 등록';
    return htmlSolaWithInstance(this)`
      <div class="page page-lead-create">
        <header class="page-header">
          <div>
            <h1>${heading}</h1>
          </div>
          <div class="page-header-actions">
            <a class="btn-ghost" data-link href="/leads">목록으로</a>
          </div>
        </header>

        <section class="form-section">
          <h2 class="form-section-title">고객 정보</h2>
          <div class="form-grid">
            <label class="form-field form-field-wide">
              <span class="form-label">고객사 <span class="required">*</span></span>
              ${this.customerAC}
              <span class="customer-label" data-bind="customerLabel">${escapeHtml(this.data.customerLabel)}</span>
              <span class="form-error" data-bind="customerError">${escapeHtml(this.data.customerError)}</span>
            </label>

            <label class="form-field">
              <span class="form-label">담당자 연락처</span>
              <input class="form-input" data-action="customer-contact"
                     placeholder="이메일 · 전화 · 카톡 ID"
                     value="${escapeHtml(this.data.customer_contact)}" />
            </label>

            <label class="form-field">
              <span class="form-label">채널 <span class="required">*</span></span>
              <select class="form-input" data-action="channel">
                <option value="homepage">홈페이지</option>
                <option value="kakao" selected>카톡</option>
                <option value="email">이메일</option>
                <option value="phone">유선 (전화)</option>
                <option value="other">기타</option>
              </select>
            </label>

            <label class="form-field">
              <span class="form-label">담당자</span>
              <input class="form-input" data-action="assigned-to"
                     placeholder="등록자/담당자 이름"
                     value="${escapeHtml(this.data.assigned_to)}" />
            </label>

            <label class="form-field form-field-wide">
              <span class="form-label">문의 내용</span>
              <textarea class="form-input" data-action="inquiry-summary"
                        rows="3"
                        placeholder="고객이 요청한 내용 요약">${escapeHtml(this.data.inquiry_summary)}</textarea>
            </label>
          </div>
        </section>

        <section class="form-section">
          <h2 class="form-section-title">요청 품목 <span class="form-section-count" data-bind="itemCount">(${this.data.itemCount}개)</span></h2>
          ${this.itemsSheet}
          <p class="datasheet-hint">
            ⌨ <strong>↑↓←→ Tab Enter</strong> 셀 이동 ·
            <strong>F2</strong> 또는 typing → 편집 모드 ·
            <strong>Esc</strong>/<strong>Enter</strong> 편집 종료 ·
            <strong>Shift+↑↓←→</strong> 영역 선택 ·
            <strong>Ctrl+C</strong> 영역 복사 ·
            <strong>Ctrl+V</strong> 엑셀 paste ·
            <strong>＋</strong> 행 추가 ·
            <strong>×</strong> 행 삭제 ·
            견적요청 토글 <strong>Space</strong>/클릭 ·
            현재 재고는 표시 전용
          </p>
        </section>

        <section class="form-section">
          <h2 class="form-section-title">영업 정보</h2>
          <div class="form-grid">
            <label class="form-field">
              <span class="form-label">상태 <span class="required">*</span></span>
              <select class="form-input" data-action="status">
                <option value="inquiry" selected>문의</option>
                <option value="quoted">가견적</option>
                <option value="delivered">배송완료</option>
                <option value="paid">입금완료</option>
                <option value="closed">완료</option>
                <option value="lost">취소</option>
              </select>
            </label>

            <label class="form-field">
              <span class="form-label">예상 마감일</span>
              <input class="form-input" type="date" data-action="expected-close-date"
                     value="${escapeHtml(this.data.expected_close_date)}" />
            </label>
          </div>
        </section>

        <div class="form-error-banner" data-bind="errorMessage">${escapeHtml(this.data.errorMessage)}</div>

        <footer class="form-actions">
          <a class="btn-ghost" data-action="cancel" data-link href="/leads">취소</a>
          <button class="btn-primary" data-action="save">${isEdit ? '변경사항 저장' : '저장'}</button>
        </footer>
      </div>
    `;
  }

  // ===== Customer =====

  private async handleCustomerSelect(
    item: api.CustomerRow | FreeTextItem,
  ): Promise<void> {
    if ('__new' in item) {
      // "신규 customer 로 등록" 선택
      try {
        const created = await api.createCustomer({ name: item.query });
        this.setSelectedCustomer(created);
      } catch (err) {
        this.setError(`신규 customer 생성 실패: ${(err as Error).message}`);
      }
    } else {
      this.setSelectedCustomer(item);
    }
  }

  private setSelectedCustomer(c: api.CustomerRow): void {
    this.selectedCustomer = c;
    this.customerAC.setValue(c.name);
    this.data.customerLabel = `✓ ${c.name} (#${c.id})${c.business_no ? ` · 사업자번호 ${c.business_no}` : ''}`;
    this.data.customerError = '';
    this.notify('customerLabel');
    this.notify('customerError');
  }

  // ===== 입력 핸들러 =====

  handleCustomerContactInput(_e: Event, target: HTMLElement): void {
    this.data.customer_contact = (target as HTMLInputElement).value;
  }

  handleAssignedToInput(_e: Event, target: HTMLElement): void {
    this.data.assigned_to = (target as HTMLInputElement).value;
  }

  handleInquirySummaryInput(_e: Event, target: HTMLElement): void {
    this.data.inquiry_summary = (target as HTMLTextAreaElement).value;
  }

  handleExpectedCloseDateInput(_e: Event, target: HTMLElement): void {
    this.data.expected_close_date = (target as HTMLInputElement).value;
  }

  handleChannelChange(_e: Event, target: HTMLElement): void {
    this.data.inquiry_channel = (target as HTMLSelectElement).value as api.InquiryChannel;
  }

  handleStatusChange(_e: Event, target: HTMLElement): void {
    this.data.status = (target as HTMLSelectElement).value as api.LeadStatus;
  }

  // ===== 저장 / 취소 =====

  handleCancel(e: Event): void {
    // <a data-link> 가 routing 처리 — 별도 동작 불필요
    void e;
  }

  async handleSave(e: Event): Promise<void> {
    e.preventDefault();
    if (this.data.saving) return;

    // 1) validation
    if (!this.selectedCustomer) {
      this.data.customerError = '고객사를 선택하거나 신규 등록해 주세요.';
      this.notify('customerError');
      this.setError('필수 입력값 확인.');
      return;
    }

    // 2) body 구성
    //   - needs_rfq: 시트 토글 값 그대로 (default false)
    //   - current_stock: UI 표시 전용 — 서버로 보내지 않음
    //   - 수정 모드: items[i].id 가 있으면 서버에서 UPDATE, 없으면 INSERT.
    const items: Array<api.LeadItemInput & { id?: number }> = this.items.map((it, idx) => ({
      ...(it.id != null ? { id: it.id } : {}),
      category: (it.category ?? null) as api.LeadCategory | null,
      option_part: this.normalizeNullable(it.option_part),
      item_description: this.normalizeNullable(it.item_description),
      quantity: this.normalizeNumber(it.quantity),
      needs_rfq: it.needs_rfq === true,
      notes: this.normalizeNullable(it.notes),
      sort_order: idx,
    }));

    const lead: api.LeadInput = {
      customer_id: this.selectedCustomer.id,
      customer_contact: this.normalizeNullable(this.data.customer_contact),
      inquiry_channel: this.data.inquiry_channel,
      status: this.data.status,
      inquiry_summary: this.normalizeNullable(this.data.inquiry_summary),
      assigned_to: this.normalizeNullable(this.data.assigned_to),
      expected_close_date: this.normalizeNullable(this.data.expected_close_date),
    };

    // 3) 저장 — mode 별로 분기
    this.data.saving = true;
    this.setError('');
    try {
      if (this.mode === 'edit' && this.leadId != null) {
        await api.updateLeadFull(this.leadId, { lead, items });
      } else {
        await api.createLeadBulk({ lead, items });
      }
      // 4) 저장 후 /leads 로 이동
      this.navigateTo(`/leads`);
    } catch (err) {
      this.setError(`저장 실패: ${(err as Error).message}`);
    } finally {
      this.data.saving = false;
    }
  }

  // ===== 헬퍼 =====

  private normalizeNullable(v: string | null | undefined): string | null {
    const s = (v ?? '').trim();
    return s === '' ? null : s;
  }

  private normalizeNumber(v: number | null | undefined): number | null {
    if (v == null) return null;
    return Number.isFinite(v) ? v : null;
  }

  private setError(msg: string): void {
    this.data.errorMessage = msg;
    this.notify('errorMessage');
  }

  private navigateTo(path: string): void {
    // SolaRouter 의 a[data-link] 인터셉트와 동일 동작 — history.pushState
    history.pushState(null, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}
